import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { InviteCard } from "../home/WelcomePage";
import { moveSong, removeSongFromQueue, setQueueTranspose, setSongSung } from "../../actions/queueActions";
import { endSession, selectSong } from "../../actions/sessionActions";
import { deleteSet, hostSet, moveSongInSet, removeSongFromSet, renameSet, setSetTranspose } from "../../actions/setsActions";
import {
  selectCurrentVerse,
  selectCurrentVerseIndex,
  selectIsHost,
  selectLiveSetName,
  selectQueueSongs,
  selectSetById,
} from "../../selectors";
import { transposeNote } from "../../lib/chords";
import { verseLabel } from "../../lib/lyrics";
import { MAX_SEMITONES, MIN_SEMITONES } from "../../lib/preferences";

const songsWord = (n) => `${n} ${n === 1 ? "song" : "songs"}`;

/** One song in a set: play (live set), position, title and key, reorder, remove. */
const SetRow = ({ song, index, count, isLive, canEdit, canPlay, onPlay, onMove, onRemove, onKey, sub }) => (
  <li className={`row${isLive ? " row-live" : ""}`}>
    {canPlay && (
      <button type="button" className={`play-btn${isLive ? " on" : ""}`} onClick={onPlay} disabled={isLive} aria-label={isLive ? `${song.Title} is on screens` : `Sing ${song.Title} now`}>
        {isLive ? "◉" : "▶"}
      </button>
    )}
    <span className="row-num">{index + 1}</span>
    <div className="row-main">
      <div className="row-title" style={{ fontSize: 16 }}>
        {song.Title}
      </div>
      <div className="row-sub">{sub}</div>
    </div>
    {canEdit && song.Key && (
      <div className="stepper" role="group" aria-label={`Key of ${song.Title}`} style={{ height: 30 }}>
        <button type="button" onClick={() => onKey(-1)} aria-label="Lower the key" disabled={(song.Transpose || 0) <= MIN_SEMITONES} style={{ width: 26, height: 30 }}>
          −
        </button>
        <span style={{ minWidth: 30, padding: "0 4px" }}>{transposeNote(song.Key, song.Transpose || 0)}</span>
        <button type="button" onClick={() => onKey(1)} aria-label="Raise the key" disabled={(song.Transpose || 0) >= MAX_SEMITONES} style={{ width: 26, height: 30 }}>
          ＋
        </button>
      </div>
    )}
    {canEdit && (
      <>
        <div className="arrows">
          <button type="button" onClick={() => onMove("up")} disabled={index === 0} aria-label={`Move ${song.Title} up`}>
            ▲
          </button>
          <button type="button" onClick={() => onMove("down")} disabled={index === count - 1} aria-label={`Move ${song.Title} down`}>
            ▼
          </button>
        </div>
        <button type="button" className="remove-btn" onClick={onRemove} aria-label={`Remove ${song.Title}`}>
          ×
        </button>
      </>
    )}
  </li>
);

/** The live set (the session's queue) with skips and the join code. */
const LiveSetEditor = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const session = useSelector((state) => state.session);
  const isHost = useSelector(selectIsHost);
  const songs = useSelector(selectQueueSongs);
  const name = useSelector(selectLiveSetName);
  const verse = useSelector(selectCurrentVerse);
  const verseIndex = useSelector(selectCurrentVerseIndex);

  const end = async () => {
    if (!window.confirm("End the session for everyone?")) return;
    await dispatch(endSession(session.Code));
  };

  return (
    <div className="page" data-screen-label="Live set">
      <button type="button" className="back-link" onClick={() => navigate("/sets")}>
        ← Sets
      </button>
      <div className="page-head">
        <h2 className="page-title" style={{ fontSize: 26 }}>
          {name}
        </h2>
        <p className="page-sub">
          {songsWord(songs.length)} · live now
          {isHost ? " · reorder with the arrows" : ""}
        </p>
      </div>
      {songs.length === 0 ? (
        <div className="empty">{isHost ? "Nothing queued yet. Add songs from the Songs page, or from the sheet on the live page." : "The host has not queued any songs yet."}</div>
      ) : (
        <ol className="stack stack-tight">
          {songs.map((song, index) => {
            const isLive = song.Id === session.SongId;
            const skipped = Boolean(song.Sung) && !isLive;
            const sub = isLive
              ? `On screens now · ${verseLabel(verse, verseIndex)}`
              : [song.Key ? transposeNote(song.Key, song.Transpose || 0) + (song.Transpose ? ` (written ${song.Key})` : "") : null, song.Verses?.length ? `${song.Verses.length} ${song.Verses.length === 1 ? "section" : "sections"}` : null, skipped ? "skipped" : null]
                  .filter(Boolean)
                  .join(" · ");
            return (
              <SetRow
                key={song.Id}
                song={song}
                index={index}
                count={songs.length}
                isLive={isLive}
                canEdit={isHost}
                canPlay={isHost}
                sub={sub}
                onPlay={() => {
                  dispatch(selectSong(session.Code, song.Id, 0));
                  navigate("/lyrics");
                }}
                onMove={(direction) => dispatch(moveSong(session.Code, song.Id, direction))}
                onRemove={() => dispatch(removeSongFromQueue(session.Code, song.Id))}
                onKey={(delta) => dispatch(setQueueTranspose(session.Code, song.Id, (song.Transpose || 0) + delta))}
              />
            );
          })}
        </ol>
      )}
      {isHost && songs.some((song) => song.Sung && song.Id !== session.SongId) && (
        <p className="note">
          Skipped songs stay in the set; the → button on the live page passes over them.{" "}
          {songs
            .filter((song) => song.Sung && song.Id !== session.SongId)
            .map((song) => (
              <button key={song.Id} type="button" className="link-btn" onClick={() => dispatch(setSongSung(session.Code, song.Id, false))}>
                Undo {song.Title}
              </button>
            ))}
        </p>
      )}
      <InviteCard compact />
      {isHost && (
        <div className="btn-row">
          <button type="button" className="btn btn-md btn-outline" onClick={() => navigate("/songs")}>
            Add a song
          </button>
          <button type="button" className="btn btn-md btn-dark" onClick={end}>
            End session
          </button>
        </div>
      )}
    </div>
  );
};

/** A saved set: reorder, remove, key per song, rename, delete, host it. */
const SavedSetEditor = ({ setId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const set = useSelector((state) => selectSetById(state, setId));
  const isLoaded = useSelector((state) => state.sets.isLoaded);
  const code = useSelector((state) => state.session.Code);
  const [busy, setBusy] = useState(false);

  if (!set) {
    return (
      <div className="page">
        <button type="button" className="back-link" onClick={() => navigate("/sets")}>
          ← Sets
        </button>
        <div className="empty">{isLoaded ? "That set is no longer there." : "Loading…"}</div>
      </div>
    );
  }

  const rename = async () => {
    const name = window.prompt("Name of the set", set.Name);
    if (!name || !name.trim() || name.trim() === set.Name) return;
    await dispatch(renameSet(set.Id, name.trim()));
  };

  const remove = async () => {
    if (!window.confirm(`Delete “${set.Name}”? The songs stay in the lists.`)) return;
    if (await dispatch(deleteSet(set.Id))) navigate("/sets", { replace: true });
  };

  const host = async () => {
    if (code && !window.confirm(`Start a new session with “${set.Name}”? The current session stays as it is.`)) return;
    setBusy(true);
    const session = await dispatch(hostSet(set.Id));
    setBusy(false);
    if (session) navigate("/lyrics");
  };

  return (
    <div className="page" data-screen-label="Set editor">
      <button type="button" className="back-link" onClick={() => navigate("/sets")}>
        ← Sets
      </button>
      <div className="page-head">
        <h2 className="page-title" style={{ fontSize: 26 }}>
          {set.Name}
        </h2>
        <p className="page-sub">
          {songsWord(set.Songs.length)} · about {set.Songs.length * 4} min · reorder with the arrows
        </p>
      </div>
      {set.Songs.length === 0 ? (
        <div className="empty">Empty so far. Bookmark songs into it from the Songs page.</div>
      ) : (
        <ol className="stack stack-tight">
          {set.Songs.map((song, index) => (
            <SetRow
              key={song.Id}
              song={song}
              index={index}
              count={set.Songs.length}
              isLive={false}
              canEdit
              canPlay={false}
              sub={[song.Artist, song.Key ? `${transposeNote(song.Key, song.Transpose || 0)}${song.Transpose ? ` (written ${song.Key})` : ""}` : null].filter(Boolean).join(" · ")}
              onMove={(direction) => dispatch(moveSongInSet(set.Id, song.Id, direction))}
              onRemove={() => dispatch(removeSongFromSet(set.Id, song.Id))}
              onKey={(delta) => dispatch(setSetTranspose(set.Id, song.Id, (song.Transpose || 0) + delta))}
            />
          ))}
        </ol>
      )}
      <div className="wrap">
        <button type="button" className="btn btn-sm btn-outline" onClick={rename}>
          Rename
        </button>
        <button type="button" className="btn btn-sm btn-danger" onClick={remove}>
          Delete set
        </button>
      </div>
      <div style={{ flex: 1 }} />
      <button type="button" className="btn btn-lg btn-dark btn-block" onClick={host} disabled={busy || set.Songs.length === 0}>
        {busy ? "Starting…" : "Host this set"}
      </button>
    </div>
  );
};

/** /queue is the live set; /sets/:id a saved one. */
const SetEditorPage = ({ live = false }) => {
  const { id } = useParams();
  if (live) return <LiveSetEditor />;
  return <SavedSetEditor setId={Number(id)} />;
};

export default SetEditorPage;
